import {ConfigProvider, Empty, message, Pagination, Table} from 'antd';
import React, { useEffect, useState } from 'react';
import { MakeApiCall } from '../../../../../apis';
import { ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import ProductDetailsModal from "../../../../../components/modal";
import {getAmount} from "../../../../../utils/helper";

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const Products = ({ userData, selectedMarketplace, selectedCurrencySign, selectedTileData }) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [totalPage, setTotalPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const [filter, setFilter] = useState({
        page: 1,
        "per-page": defaultPageSize,
        marketplace_id: selectedMarketplace?.id,
        start_date: selectedTileData?.start_date,
        end_date: selectedTileData?.end_date,
        sku: selectedTileData?.sku
    });

    const getProductList = async (currentFilter) => {
        if (!currentFilter?.marketplace_id) {
            setList([]);
            setTotalPage(0);
            return;
        }

        setLoading(true);
        const response = await MakeApiCall(
            `dashboard/tiles/get-product-analytics-data${ConvertParams(currentFilter)}`,
            "get",
            null,
            true,
            {},
            userData?.token
        );

        if (response?.status) {
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            setPage(currentFilter.page);
            setPageSize(currentFilter['per-page']); // Ensure pageSize is also synced
            const records = response?.data?.records?.summaryData || [];
            setList(records);
        } else {
            message.warning(response?.message);
            setList([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        const newMarketplaceId = selectedMarketplace?.id;
        const newStartDate = selectedTileData?.start_date;
        const newEndDate = selectedTileData?.end_date;
        const newSku = selectedTileData?.sku;

        if (
            filter.marketplace_id !== newMarketplaceId ||
            filter.start_date !== newStartDate ||
            filter.end_date !== newEndDate ||
            filter.sku !== newSku
        ) {
            // Update the filter state and reset to page 1
            setFilter(prev => ({
                ...prev,
                page: 1, // Reset to page 1 on filter change
                marketplace_id: newMarketplaceId,
                start_date: newStartDate,
                end_date: newEndDate,
                sku: newSku
            }));
            // If the props match the initial state, setFilter is NOT called, preventing Call 2.
        }
    }, [selectedMarketplace, selectedTileData]); // Depend on the objects/props directly

    useEffect(() => {
        if (filter.marketplace_id && filter.start_date && filter.end_date) {
            getProductList(filter);
        }
    }, [filter, userData?.token]);

    const columns = [
        {
            title: "#",
            key: "index",
            width: 60,
            render: (text, record, index) => (filter.page - 1) * filter["per-page"] + index + 1,
        },
        {
            title: "User Details",
            dataIndex: "userDetails",
            key: "userDetails",
            width: 450,
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.image_url || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <a
                           className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-250px"
                           href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"}
                           target="_blank"
                           rel="noopener noreferrer"
                        >{record?.product_title || ''}
                        </a>
                        <div className="fs-7 mt-1"><span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span></div>
                        <div className="fs-7 mt-1">
                            <span><b>{getAmount(record?.price, selectedCurrencySign)}</b></span> /
                            <span>{record?.fulfillment_channel}</span> /
                            <span>COGS {getAmount(record?.cogs, selectedCurrencySign)}</span>
                        </div>
                    </div>
                </div>
            ),
        },
        { title: "Units Sold", dataIndex: "units", key: "units", sorter: (a, b) => a.units - b.units },
        { title: "Refund Units", dataIndex: "refund_unit", key: "refund_unit", sorter: (a, b) => a.refund_unit - b.refund_unit },
        {
            title: "Refund Cost",
            dataIndex: "refund_cost",
            key: "refund_cost",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.refund_cost - b.refund_cost
        },
        {
            title: "Sales",
            dataIndex: "sales",
            key: "sales",
            render: (text) => <span className="text-gray-800 fw-bolder">{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.sales - b.sales
        },
        {
            title: "Ad Sales",
            dataIndex: "ad_sales",
            key: "ad_sales",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.ad_sales - b.ad_sales
        },
        {
            title: "Ad Spend",
            dataIndex: "ad_spend",
            key: "ad_spend",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.ad_spend - b.ad_spend
        },
        { title: "ROAS", dataIndex: "roas", key: "roas", render: (text) => <span>{text.toFixed(2)}</span>, sorter: (a, b) => a.roas - b.roas },
        {
            title: "Organic Sales",
            dataIndex: "organic_sales",
            key: "organic_sales",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.organic_sales - b.organic_sales
        },
        {
            title: "Tax",
            dataIndex: "tax",
            key: "tax",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.tax - b.tax
        },
        {
            title: "Fees",
            dataIndex: "fees",
            key: "fees",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.fees - b.fees
        },
        {
            title: "Other Charges",
            dataIndex: "other_charges",
            key: "other_charges",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.other_charges - b.other_charges
        },
        {
            title: "Gross Profit",
            dataIndex: "gross_profit",
            key: "gross_profit",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.gross_profit - b.gross_profit
        },
        {
            title: "Net Profit",
            dataIndex: "net_profit",
            key: "net_profit",
            render: (text) => <span className="fw-bold text-success">{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.net_profit - b.net_profit
        },
        {
            title: "Profit/Unit",
            dataIndex: "profit_per_unit",
            key: "profit_per_unit",
            render: (text) => <span>{getAmount(text, selectedCurrencySign)}</span>,
            sorter: (a, b) => a.profit_per_unit - b.profit_per_unit
        },
        { title: "Inventory", dataIndex: "inventory", key: "inventory", sorter: (a, b) => a.inventory - b.inventory },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span className={`badge ${text === 0 ? 'badge-light-success' : 'badge-light-danger'}`}>
          {text === 0 ? 'Active' : 'Inactive'}
        </span>
            ),
        },
        {
            title: "Details",
            key: "details",
            render: (_, record) => (
                <span
                    className="link-success cursor-pointer"
                    onClick={() => {
                        setSelectedRow(record);
                        setIsModalOpen(true);
                    }}
                >
          See Details
        </span>
            ),
        },
    ];

    return (
        <div>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="table-responsive">
                        <Table
                            scroll={{ x: "max-content" }}
                            columns={columns}
                            loading={loading}
                            dataSource={list}
                            rowKey="key"
                            pagination={false}
                            locale={{ emptyText: <Empty description="No Data Found..." /> }}
                            components={{
                                table: (props) => (
                                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                                ),
                                header: {
                                    wrapper: (props) => <thead {...props} />,
                                    row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                                    cell: (props) => <th {...props} />,
                                },
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-body py-2">
                            <ConfigProvider>
                                <Pagination
                                    className="mt-3"
                                    current={filter.page}
                                    pageSize={filter['per-page']}
                                    total={totalPage}
                                    showSizeChanger
                                    onChange={(currentPage, newPageSize) => {
                                        setFilter(prev => {
                                            const newFilter = {
                                                ...prev,
                                                "per-page": newPageSize,
                                                page: newPageSize !== prev["per-page"] ? 1 : currentPage,
                                            };
                                            setPage(newFilter.page);
                                            setPageSize(newFilter["per-page"]);
                                            return newFilter;
                                        });
                                    }}
                                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                />
                            </ConfigProvider>
                        </div>
                    </div>
                </div>
            </div>
            <ProductDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRow}
                currencySign={selectedCurrencySign}
            />
        </div>
    );
};

export default Products;